import * as React from "react"
import 'intersection-observer';
import { InView } from 'react-intersection-observer';
import { Action, IOWidget, mk_widget, Widget, AsyncState, Pair, mk_pair } from "widgets-for-react"
import { CMSState, Id, ImageSize, imageGalleryBlockData } from "../types.gen";
import { getClassName } from "../utils.gen";
import { List, Map } from "immutable";
import { ImageContainer } from "./ImageContainer.gen";
import { blockInviewHandler } from "../../../drupal/rendering/inview_utils.mod";


export type ImageGalleryBlockProps<extra_images> = {
  index: number
  title?: string | React.ReactNode
  intro?: string
  images: ListWithAtLeast3Items<ImageGalleryImageProps<extra_images>>,
  imagePreviewCount: number,
  get_default_data_src:(() => ImageSize<extra_images>),
  get_default_data_srcset:(() => Pair<ImageSize<extra_images>, string>[])
  copy: ImageGalleryCopy
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
}

export type ImageGalleryImageProps<extra_images> = {
  index: number
  image: Id
  image_size: ImageSize<extra_images>
  imageDataSizes?: string
  image_alt: string
  is_foreground:boolean
  caption?: string
  source?: string
}

export type ImageGalleryCopy = {
  close: string,
  more: string
  enlarged: string
  previous: string
  next: string
}

export type ImageGalleryBlockStyle = Partial<{
  block_classname: string
  inview_classname: string
  inner_classname: string
  content_classname: string
  header_classname: string
  title_classname: string
  intro_classname: string
  gallery_list_classname: string
}> & ImageGalleryItemStyle

const ondoneImage = <CustomBlockData, extra_images>(
  onDone: (_: Action<CMSState<CustomBlockData, extra_images, never>>) => void,
  props: ImageGalleryBlockProps<extra_images>,
  index: number
) => (action: Action<"next" | "prev" | "close" | "open">) => {
  let openNew = action("open")
  onDone(s1 => {
    let currentBlock = s1.blocks.get(props.index) as imageGalleryBlockData<extra_images>
    if (currentBlock.kind === "imageGalleryBlock") {
      currentBlock = {
        ...currentBlock,
        value: mk_pair(
          {
            ...currentBlock.value.fst,
            images: new ListWithAtLeast3Items<ImageGalleryImageProps<extra_images>>(
              currentBlock.value.fst.images.items.map((b, b_i) => {
                if (openNew == "next" && b_i - 1 == index) {
                  return { ...b, is_foreground: true }
                }
                if (openNew == "prev" && b_i + 1 == index) {
                  return { ...b, is_foreground: true }
                }
                if (openNew == "open" && b_i == index) {
                  return { ...b, is_foreground: true }
                }
                return { ...b, is_foreground: false }
              }).toList()
            )
          },
          currentBlock.value.snd
        ),
      }
    }
    return { ...s1, blocks: s1.blocks.set(props.index, currentBlock) }
  })
}


export function ImageGalleryBlock<CustomBlockData, extra_images>(
  props: ImageGalleryBlockProps<extra_images>,
  options?: { style?: ImageGalleryBlockStyle; key?: string }
): IOWidget<CMSState<CustomBlockData, extra_images, never>, Action<CMSState<CustomBlockData, extra_images, never>>> {
  return (s: CMSState<CustomBlockData, extra_images, never>) =>
    mk_widget({
      run: onDone => {
        let images = props.images.GetFirst(props.imagePreviewCount)
        let openImage = props.images.items.find(i => i.is_foreground)
        let openImageIndex = props.images.items.findIndex(i => i.is_foreground)

        return (
          <InView
            as="section"
            onChange={(inView) => blockInviewHandler('imageGallery', inView, `io-${options && options.key ? options.key : props.index}`, getClassName(options, "inview_classname", "inview"))}
            triggerOnce={props.inview && props.inview.triggerOnce ? props.inview.triggerOnce : false}
            threshold={props.inview && props.inview.threshold ? props.inview.threshold : 0.1}
            id={`io-${options && options.key ? options.key : props.index}`}
            className={getClassName(options, "block_classname", "container")}
            key={options && options.key ? options.key : `block-${props.index}`}
          >
            <div className={getClassName(options, "inner_classname", "py-5")}>
              {(props.title || props.intro) && (
                <header className={options && options.style ? options.style.header_classname : ""}>
                  {props.title && (<h2 className={getClassName(options, "title_classname", "mb-3")}>{props.title}</h2>)}
                  {props.intro && (<p className={getClassName(options, "intro_classname", "")}>{props.intro}</p>)}
                </header>
              )}

              <div className={getClassName(options, "content_classname", "")}>
                <ul className={getClassName(options, "gallery_list_classname", "list-unstyled mb-0 row")}>
                  {images.items.map((value, index) =>
                    ImageGalleryItem(
                      s,
                      {
                        index: props.index,
                        is_first: index == 0,
                        is_last: index + 1 >= props.images.items.count(),
                        id: value.image,
                        image: value.image_size,
                        image_alt: value.image_alt,
                        key: value.image + `${index}`,
                        get_default_data_src: props.get_default_data_src,
                        get_default_data_srcset: props.get_default_data_srcset,
                        additional_info: index + 1 >= images.items.count() && images.remaining_items > 0 ? `+${images.remaining_items}` : undefined,
                        caption: value.caption,
                        source: value.source,
                        copy: props.copy,
                        isOpen: value.is_foreground,
                        imageDataSizes: value.imageDataSizes
                      },
                      index,
                      options
                    ).run(ondoneImage(onDone, props, index))
                  )}
                </ul>
              </div>

              {openImage && ImageGalleryOverlay(
                s,
                {
                  index: props.index,
                  is_first: openImageIndex == 0,
                  is_last: openImageIndex + 1 >= props.images.items.count(),
                  id: openImage.image,
                  image: openImage.image_size,
                  image_alt: openImage.image_alt,
                  key: openImage.image + `${openImageIndex}`,
                  get_default_data_src: props.get_default_data_src,
                  get_default_data_srcset: props.get_default_data_srcset,
                  additional_info: openImageIndex + 1 >= images.items.count() && images.remaining_items > 0 ? `+${images.remaining_items}` : undefined,
                  caption: openImage.caption,
                  source: openImage.source,
                  copy: props.copy,
                  isOpen: openImage.is_foreground,
                  imageDataSizes: openImage.imageDataSizes
                },
                openImageIndex,
                options
              ).run(ondoneImage(onDone, props, openImageIndex))}
            </div>
          </InView>
        )
      }
    })
}
  

export type ImageGalleryItemProps<extra_images> =  {
  index: number
  is_first: boolean
  is_last: boolean
  key?: string
  id: Id
  image: ImageSize<extra_images>
  image_alt: string
  imageDataSizes?: string
  get_default_data_src: (() => ImageSize<extra_images>)
  get_default_data_srcset: (() => Pair<ImageSize<extra_images>, string>[])
  additional_info?: string
  caption?: string
  source?: string
  copy: ImageGalleryCopy
  isOpen: boolean
}


export type ImageGalleryItemStyle = Partial<{
  gallery_item_classname: string
  gallery_button_classname: string
  gallery_image_classname: string
  gallery_toggle_classname: string
  lightbox_classname: string
  lightbox_inner_classname: string
  lightbox_figure_classname: string
  lightbox_visual_classname: string
  lightbox_image_classname: string
  lightbox_figcaption_classname: string
  lightbox_caption_classname: string
  lightbox_source_classname: string
  lightbox_nav_classname: string
  lightbox_nav_inner_classname: string
  lightbox_controls_classname: string
  lightbox_controls_inner_classname: string
  lightbox_next_classname: string
  lightbox_prev_classname: string
  lightbox_close_classname: string
  icon_close_id: string
  icon_previous_id: string
  icon_next_id: string
}>


function ImageGalleryItem<customBlockData, extra_images, never>(
  cmsState: CMSState<customBlockData, extra_images, never>,
  props: ImageGalleryItemProps<extra_images>,
  index: number,
  options?: { style?: ImageGalleryItemStyle; key?: string },
): Widget<Action<"next" | "prev" | "close" | "open">> {
  return mk_widget({
    run: onDone => {
      return (
        <li className={getClassName(options, "gallery_item_classname", "col-sm-6 col-md-4 pb-3")} key={`image-${options && options.key ? options.key : props.index}-${index}`}>
          <button
            onClick={e => { onDone(_ => "open"); e.stopPropagation(); document.documentElement.classList.add('lightbox--open'); }}
            className={getClassName(options, "gallery_button_classname", "btn p-0 w-100") + (props.additional_info ? " has-toggle" : '')}
            aria-expanded={props.isOpen}
            aria-controls={`a11y-lightbox-${props.id}`}
          >
            {props.isOpen}
            {cmsState.images.has(props.id) &&
              ImageContainer(
                cmsState.images.get(props.id) as AsyncState<Map<ImageSize<extra_images>, string>>,
                props.image,
                getClassName(options, "gallery_image_classname", "w-100"),
                props.image_alt,
                props.get_default_data_src,
                props.get_default_data_srcset
              ).run(_ => { })}
            {props.additional_info ? (
              <div className={getClassName(options, "gallery_toggle_classname", "")}>
                {props.additional_info}
                <span className="sr-text">{props.copy.more}</span>
              </div>
            ) : (
              <span className="sr-text">{props.copy.enlarged}</span>
            )}
          </button>
        </li>
      )
    }
  })
}


function ImageGalleryOverlay<customBlockData, extra_images, never>(
  cmsState: CMSState<customBlockData, extra_images, never>,
  props: ImageGalleryItemProps<extra_images>,
  index: number,
  options?: { style?: ImageGalleryItemStyle; key?: string },
): Widget<Action<"next" | "prev" | "close" | "open">> {
  return mk_widget({
    run: onDone => {
      return (
        <div
          id={`a11y-lightbox-${props.id}`}
          aria-hidden="true"
          className={getClassName(options, "lightbox_classname", "mt-3 p-5 bg-light")}
          onClick={(e) => { onDone(_ => "close");  e.stopPropagation(); document.documentElement.classList.remove('lightbox--open'); }}
          key={`overlay-${options && options.key ? options.key : props.index}-${index}`}
        >
          <div className={getClassName(options, "lightbox_inner_classname", "")}>
            <div className={getClassName(options, "lightbox_nav_classname", "")}>
              <div className={getClassName(options, "lightbox_nav_inner_classname", "")}>
                <button
                  onClick={(e) => { onDone(_ => "close"); e.stopPropagation(); document.documentElement.classList.remove('lightbox--open'); }}
                  className={getClassName(options, "lightbox_close_classname", "btn btn-outline-dark d-block ml-auto")}
                >
                  <span className="sr-text">{props.copy.close}</span>
                  <svg aria-hidden="true"><use xlinkHref={getClassName(options, "icon_close_id", "#icon--cross")} /></svg>
                </button>
              </div>
            </div>
            
            <figure className={getClassName(options, "lightbox_figure_classname", "mt-3")}>
              <div className={getClassName(options, "lightbox_visual_classname", "")}>
                {cmsState.images.has(props.id) &&
                  ImageContainer(
                    cmsState.images.get(props.id) as AsyncState<Map<ImageSize<extra_images>, string>>,
                    props.image,
                    getClassName(options, "lightbox_image_classname", "w-100"),
                    props.image_alt,
                    props.get_default_data_src,
                    props.get_default_data_srcset,
                    props.imageDataSizes
                  ).run(_ => { })}
                {(props.caption || props.source) && (
                  <figcaption className={getClassName(options, "lightbox_figcaption_classname", "mt-2 text-center")}>
                    {props.caption ? <p className={getClassName(options, "lightbox_caption_classname", "m-0 small text-secondary")}>{props.caption}</p> : <></>}
                    {props.source ? <p className={getClassName(options, "lightbox_source_classname", "m-0 small text-secondary")}>{props.source}</p> : <></>}
                  </figcaption>
                )}
              </div>
            </figure>

            <div className={getClassName(options, "lightbox_controls_classname", "")}>
              <div className={getClassName(options, "lightbox_controls_inner_classname", "d-flex justify-content-between")}>
                {!props.is_first
                  ? <button
                      onClick={e => { onDone(_ => "prev"); e.stopPropagation(); }}
                      className={getClassName(options, "lightbox_prev_classname", "btn btn-dark")}
                    >
                      <span className="sr-text">{props.copy.previous}</span>
                      <svg aria-hidden="true"><use xlinkHref={getClassName(options, "icon_previous_id", "#icon--chevron-left-large")} /></svg>
                    </button>
                  : ""
                }
                {!props.is_last
                  ? <button
                      onClick={e => { onDone(_ => "next"); e.stopPropagation(); }}
                      className={getClassName(options, "lightbox_next_classname", "btn btn-dark ml-auto")}
                    >
                      <span className="sr-text">{props.copy.next}</span>
                      <svg aria-hidden="true"><use xlinkHref={getClassName(options, "icon_next_id", "#icon--chevron-right-large")} /></svg>
                    </button>
                  : ""
                }
              </div>
            </div>
          </div>
        </div>
      )
    }
  })
}
  

export class ListWithAtLeast3Items<T> {
  public items: List<T>
  public constructor(images: List<T>) {
    if (images.count() < 3) {
      throw `ListWithAtLeast3Items: expected at least 3 items, but received ${images.count()}`
    }
    this.items = images
  }
  public GetFirst(n: number): { items: List<T>, remaining_items: number } {
    if (this.items.count() <= n) return { items: this.items, remaining_items: 0 }
    let selected_images = this.items.take(n).toList()
    return { items: selected_images, remaining_items: this.items.count() - selected_images.count() }
  }
}
